// src/utils/permissions.ts
import { 
  USER_TYPE_SYSTEM_ADMIN,
  USER_TYPE_EDIFY_STAFF,
  USER_TYPE_PARISH_COORDINATOR,
  USER_TYPE_SCHOOL_PROPRIETOR,
  USER_TYPE_SCHOOL_DIRECTOR,
  USER_TYPE_TEACHER,
  USER_TYPE_ACCOUNTANT,
  USER_TYPE_IT_TEACHER,
  USER_TYPE_CT_TEACHER
} from '../api/constants';

// Use a flexible user type that works with both AuthUser and IUser
export interface PermissionUser {
  id?: number;
  email?: string;
  user_type?: string;  // Made optional to accept Record<string, any>
  is_active?: boolean;
  is_verified?: boolean;
  profile?: {
    parish?: number;
    school?: number;
  };
  [key: string]: any; // Allow additional properties
}

export interface ISchool {
  id: number;
  parish: number;
}

export interface IParish {
  id: number;
}

// ============================================
// SYSTEM-LEVEL PERMISSIONS
// ============================================

export const IsSystemAdmin = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  return user.user_type === USER_TYPE_SYSTEM_ADMIN;
};

export const IsEdifyStaff = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  return user.user_type === USER_TYPE_EDIFY_STAFF;
};

export const IsSystemAdminOrEdifyStaff = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  return user.user_type === USER_TYPE_SYSTEM_ADMIN || 
         user.user_type === USER_TYPE_EDIFY_STAFF;
};

// ============================================
// PARISH-LEVEL PERMISSIONS
// ============================================

export const IsParishCoordinator = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  return user.user_type === USER_TYPE_PARISH_COORDINATOR;
};

export const CanManageParish = (user?: PermissionUser | null, parishId?: number): boolean => {
  if (!user || !user.user_type) return false;
  
  // System admin and Edify staff can manage any parish
  if (IsSystemAdminOrEdifyStaff(user)) return true;
  
  // Parish coordinator can manage their assigned parish
  if (user.user_type === USER_TYPE_PARISH_COORDINATOR) {
    if (!parishId) return true; // General permission check
    return user.profile?.parish === parishId;
  }
  
  return false;
};

export const CanAccessParishData = (user?: PermissionUser | null, parishId?: number): boolean => {
  if (!user || !user.user_type) return false;
  
  // System admin and Edify staff can access all parishes
  if (IsSystemAdminOrEdifyStaff(user)) return true;
  
  // Parish coordinator can access their parish
  if (user.user_type === USER_TYPE_PARISH_COORDINATOR) {
    if (!parishId) return true; // General permission check
    return user.profile?.parish === parishId;
  }
  
  // School staff can access their parish data through their school
  if (IsSchoolStaff(user) && user.profile?.school) {
    return true;
  }
  
  return false;
};

// ============================================
// SCHOOL-LEVEL PERMISSIONS
// ============================================

export const IsSchoolProprietor = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  return user.user_type === USER_TYPE_SCHOOL_PROPRIETOR;
};

export const IsSchoolDirector = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  return user.user_type === USER_TYPE_SCHOOL_DIRECTOR;
};

export const IsTeacher = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  return user.user_type === USER_TYPE_TEACHER;
};

export const IsSchoolStaff = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  
  const schoolStaffTypes = [
    USER_TYPE_SCHOOL_PROPRIETOR,
    USER_TYPE_SCHOOL_DIRECTOR,
    USER_TYPE_TEACHER,
    USER_TYPE_ACCOUNTANT,
    USER_TYPE_IT_TEACHER,
    USER_TYPE_CT_TEACHER
  ];
  
  return schoolStaffTypes.includes(user.user_type);
};

export const IsSchoolLeadership = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  
  return [
    USER_TYPE_SCHOOL_PROPRIETOR,
    USER_TYPE_SCHOOL_DIRECTOR
  ].includes(user.user_type);
};

export const CanManageSchool = (user?: PermissionUser | null, schoolId?: number): boolean => {
  if (!user || !user.user_type) return false;
  
  // System admin and Edify staff can manage any school
  if (IsSystemAdminOrEdifyStaff(user)) return true;
  
  // Parish coordinators can manage schools in their parish
  if (IsParishCoordinator(user)) return true;
  
  // School proprietor/director can manage their school
  if (IsSchoolLeadership(user)) {
    if (!schoolId) return true; // General permission check
    return user.profile?.school === schoolId;
  }
  
  return false;
};

export const CanAccessSchoolData = (user?: PermissionUser | null, schoolId?: number): boolean => {
  if (!user || !user.user_type) return false;
  
  // System admin and Edify staff can access all schools
  if (IsSystemAdminOrEdifyStaff(user)) return true;
  
  // Parish coordinator can access schools in their parish
  if (IsParishCoordinator(user)) {
    return true;
  }
  
  // School staff can access their own school data
  if (IsSchoolStaff(user)) {
    if (!schoolId) return true; // General permission check
    return user.profile?.school === schoolId;
  }
  
  return false;
};

// ============================================
// ASSESSMENT PERMISSIONS
// ============================================

export const CanCreateAssessment = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  
  // System admin, Edify staff, Parish coordinators, and School leadership can create
  return IsSystemAdminOrEdifyStaff(user) || 
         IsParishCoordinator(user) || 
         IsSchoolLeadership(user);
};

export const CanValidateAssessment = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  
  // Only system admin, Edify staff, and Parish coordinators can validate
  return IsSystemAdminOrEdifyStaff(user) || IsParishCoordinator(user);
};

export const CanSubmitAssessment = (user?: PermissionUser | null, schoolId?: number): boolean => {
  if (!user || !user.user_type) return false;
  
  // System admin and Edify staff can submit for any school
  if (IsSystemAdminOrEdifyStaff(user)) return true;
  
  // School leadership can submit for their school
  if (IsSchoolLeadership(user)) {
    if (!schoolId) return true;
    return user.profile?.school === schoolId;
  }
  
  return false;
};

// ============================================
// MEETING PERMISSIONS
// ============================================

export const CanCreateMeeting = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  
  // System admin, Edify staff, and Parish coordinators can create meetings
  return IsSystemAdminOrEdifyStaff(user) || IsParishCoordinator(user);
};

export const CanManageMeetingAttendance = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  
  // System admin, Edify staff, and Parish coordinators can manage attendance
  return IsSystemAdminOrEdifyStaff(user) || IsParishCoordinator(user);
};

export const CanAccessMeeting = (user?: PermissionUser | null): boolean => {
  // All authenticated users can access meetings
  return !!user;
};

// ============================================
// REPORTS PERMISSIONS
// ============================================

export const CanViewReports = (user?: PermissionUser | null): boolean => {
  // Everyone authenticated can view some reports, but scope differs
  return !!user;
};

export const CanViewSystemReports = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  
  // Only system admin and Edify staff can view system-wide reports
  return IsSystemAdminOrEdifyStaff(user);
};

export const CanViewParishReports = (user?: PermissionUser | null, parishId?: number): boolean => {
  if (!user || !user.user_type) return false;
  
  // System admin and Edify staff can view all parish reports
  if (IsSystemAdminOrEdifyStaff(user)) return true;
  
  // Parish coordinator can view their parish reports
  if (IsParishCoordinator(user)) {
    if (!parishId) return true;
    return user.profile?.parish === parishId;
  }
  
  return false;
};

export const CanViewSchoolReports = (user?: PermissionUser | null, schoolId?: number): boolean => {
  if (!user || !user.user_type) return false;
  
  // System admin and Edify staff can view all school reports
  if (IsSystemAdminOrEdifyStaff(user)) return true;
  
  // Parish coordinators can view schools in their parish
  if (IsParishCoordinator(user)) return true;
  
  // School staff can view their school reports
  if (IsSchoolStaff(user)) {
    if (!schoolId) return true;
    return user.profile?.school === schoolId;
  }
  
  return false;
};

// ============================================
// FINANCIAL PERMISSIONS
// ============================================

export const CanViewFinancials = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  
  // Only system admin and Edify staff can view financials
  return IsSystemAdminOrEdifyStaff(user);
};

export const CanManageSubscriptions = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  
  // Only system admin and Edify staff can manage subscriptions
  return IsSystemAdminOrEdifyStaff(user);
};

// ============================================
// USER MANAGEMENT PERMISSIONS
// ============================================

export const CanManageUsers = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  
  // Only system admins can manage all users
  return IsSystemAdmin(user);
};

export const CanManageCoordinators = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  
  // System admin and Edify staff can manage coordinators
  return IsSystemAdminOrEdifyStaff(user);
};

export const CanViewUserList = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  
  // System admin, Edify staff, and Parish coordinators can view user lists
  return IsSystemAdminOrEdifyStaff(user) || IsParishCoordinator(user);
};

// ============================================
// TRAINING PERMISSIONS
// ============================================

export const CanCreateTraining = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  
  // System admin, Edify staff, and Parish coordinators can create training
  return IsSystemAdminOrEdifyStaff(user) || IsParishCoordinator(user);
};

export const CanManageTraining = (user?: PermissionUser | null): boolean => {
  if (!user || !user.user_type) return false;
  
  // System admin, Edify staff, and Parish coordinators can manage training
  return IsSystemAdminOrEdifyStaff(user) || IsParishCoordinator(user);
};

export const CanAccessTraining = (user?: PermissionUser | null): boolean => {
  // All authenticated users can access training
  return !!user;
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

export const HasAnyRole = (user?: PermissionUser | null, roles: string[] = []): boolean => {
  if (!user || !user.user_type || !roles.length) return false;
  return roles.includes(user.user_type);
};

export const GetUserPermissionLevel = (user?: PermissionUser | null): number => {
  if (!user || !user.user_type) return 0;
  
  // Permission hierarchy (higher number = more permissions)
  const levels: Record<string, number> = {
    [USER_TYPE_SYSTEM_ADMIN]: 100,
    [USER_TYPE_EDIFY_STAFF]: 90,
    [USER_TYPE_PARISH_COORDINATOR]: 70,
    [USER_TYPE_SCHOOL_PROPRIETOR]: 60,
    [USER_TYPE_SCHOOL_DIRECTOR]: 50,
    [USER_TYPE_ACCOUNTANT]: 40,
    [USER_TYPE_IT_TEACHER]: 30,
    [USER_TYPE_CT_TEACHER]: 30,
    [USER_TYPE_TEACHER]: 20,
  };
  
  return levels[user.user_type] || 0;
};

// ============================================
// ROUTE PERMISSION CHECKS
// ============================================

export const CanAccessRoute = (user?: PermissionUser | null, requiredRoles?: string[]): boolean => {
  if (!user || !user.user_type) return false;
  if (!requiredRoles || requiredRoles.length === 0) return true;
  
  return HasAnyRole(user, requiredRoles);
};